import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import { assetService } from "../../../../services/api/assetService";
import { buildAssetDateParams } from "../utils/assetFilterUtils";
import {
  buildAssetReportRows,
  getAssetReportSummary,
  normalizeAssetChart,
} from "../utils/assetReportFormatters";

export const useAssetReport = ({
  enabled = true,
  fromDate = "",
  toDate = "",
} = {}) => {
  const { token_client } = useContext(AuthContext);
  const requestIdRef = useRef(0);
  const [chart, setChart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const reportParams = useMemo(
    () => buildAssetDateParams({ fromDate, toDate }),
    [fromDate, toDate],
  );

  const fetchReport = useCallback(async () => {
    if (!enabled) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    try {
      setLoading(true);
      setError("");

      const response = Object.keys(reportParams).length
        ? await assetService.getOriginalProfileReport(token_client, reportParams)
        : await assetService.getOriginalProfileReport(token_client);

      if (requestIdRef.current !== requestId) return;

      setChart(normalizeAssetChart(response?.chart));
    } catch (err) {
      if (requestIdRef.current !== requestId) return;

      console.error("Error fetching original asset report:", err);
      setChart([]);
      setError("Không thể tải biểu đồ tài sản. Vui lòng thử lại.");
      toast.dismiss();
      toast.error("Lỗi khi lấy biểu đồ tài sản");
    } finally {
      if (requestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [enabled, reportParams, token_client]);

  useEffect(() => {
    if (enabled) fetchReport();
  }, [enabled, fetchReport]);

  return useMemo(
    () => ({
      chart,
      error,
      loading,
      rows: buildAssetReportRows(chart),
      summary: getAssetReportSummary(chart),
      fetchReport,
    }),
    [chart, error, fetchReport, loading],
  );
};
